import React from "react";
import {
  Autocomplete,
  Checkbox,
  Chip,
  ListItemIcon,
  ListItemText,
  TextField,
} from "@mui/material";
import { FixedSizeList as List } from "react-window";

const ITEM_HEIGHT = 48;
const DEFAULT_MAX_HEIGHT = 360;
const ALL = "all";

const Ctx = React.createContext({ itemSize: ITEM_HEIGHT, maxHeight: DEFAULT_MAX_HEIGHT });

function Listbox({ children, className, ref, ...other }) {
  const { itemSize, maxHeight } = React.useContext(Ctx);
  const kids = React.Children.toArray(children);
  return (
    <div ref={ref} className={className} {...other}>
      <List
        height={Math.min(kids.length * itemSize, maxHeight)}
        width="100%"
        itemCount={kids.length}
        itemSize={itemSize}
        innerElementType="ul"
      >
        {({ index, style }) =>
          React.cloneElement(kids[index], {
            style: { ...style, ...kids[index].props.style },
          })
        }
      </List>
    </div>
  );
}

export default function VirtualizedMultiSelect({
  label,
  id,
  value = [],
  onChange,
  options = [],
  disabled = false,
  width,
  minWidth = 160,
  marginTop = 2,
  paperWidth,
  showSelectAll = false,
  selectAllChecked,
  selectAllIndeterminate,
  selectAllText = "Select All",
  formControlSx,
  itemSize = ITEM_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
}) {
  const selected = value.filter((v) => v !== ALL);
  const [inputValue, setInputValue] = React.useState("");

  const allSelected = Boolean(options.length && selected.length === options.length);
  const partial = !allSelected && selected.length > 0 && selected.length < options.length;

  const fire = (val) => onChange?.({ target: { value: val } });

  const handleChange = (_e, raw) => {
    if (!raw) raw = [];
    if (raw.includes(ALL)) {
      fire(allSelected ? [] : [...options]);
    } else {
      fire(raw);
    }
  };

  const filterOptions = (_, state) => {
    const q = (state.inputValue ?? inputValue).toLowerCase();
    const filtered = options.filter((o) => o.toLowerCase().includes(q));
    return showSelectAll ? [ALL, ...filtered] : filtered;
  };

  const acOptions = React.useMemo(
    () => (showSelectAll ? [ALL, ...options] : options),
    [showSelectAll, options],
  );

  return (
    <Ctx.Provider value={{ itemSize, maxHeight }}>
      <Autocomplete
        id={id}
        multiple
        disableCloseOnSelect
        limitTags={3}
        disabled={disabled}
        options={acOptions}
        value={selected}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, v, reason) => {
          if (reason === "input") setInputValue(v);
          else if (reason === "clear") setInputValue("");
        }}
        filterOptions={filterOptions}
        getOptionLabel={(o) => (o === ALL ? selectAllText : o)}
        renderOption={(props, opt) => {
          if (opt === ALL) {
            return (
              <li {...props} key={ALL}>
                <ListItemIcon>
                  <Checkbox
                    checked={selectAllChecked ?? allSelected}
                    indeterminate={selectAllIndeterminate ?? partial}
                  />
                </ListItemIcon>
                <ListItemText primary={selectAllText} />
              </li>
            );
          }
          return (
            <li {...props} key={`${opt}-${props["data-option-index"] ?? 0}`}>
              <Checkbox checked={selected.includes(opt)} />
              <ListItemText primary={opt} />
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) => {
          if (allSelected) {
            return [<Chip label="All" {...getTagProps({ index: 0 })} onDelete={() => fire([])} key="all" />];
          }
          return tagValue.map((opt, i) => (
            <Chip
              key={opt}
              label={opt}
              {...getTagProps({ index: i })}
              onDelete={() => fire(selected.filter((d) => d !== opt))}
            />
          ));
        }}
        renderInput={(params) => (
          <TextField {...params} label={label} size="small" sx={formControlSx} />
        )}
        ListboxComponent={Listbox}
        sx={{ width, minWidth, marginTop }}
      />
    </Ctx.Provider>
  );
}